/**
 * D-020 Live Trading V2 — the normalized one-click/hotkey order intent and its
 * SUBMIT-TIME guard.
 *
 * V1's stale-line / taker guard lived entirely in render-time pill disables:
 * `oneClickStore.submit` itself checked only price range + pacing, so nothing
 * revalidated between render and click. Every LT2 input path (mouse pill,
 * ladder level, A/D hotkey, any surface) now builds one `LiveOrderIntent` and
 * passes through `evaluateIntentGuard` against the CURRENT book at submit
 * time. The guard is pure and synchronous — books re-derive per market frame,
 * so at click time the book handed in is at most one frame old.
 *
 * Guard rules, in refusal-priority order:
 *   1. identity  — the book's ticker must equal the intent's ticker. This is
 *                  the LT2-006 "stable market identity" enforcement: a
 *                  re-sorted DOM or swapped card can never fire at the wrong
 *                  market.
 *   2. availability — classifyLineAvailability must say 'active'
 *                  (absent / mapping-error / suspended / stale all refuse,
 *                  each with its own reason).
 *   3. price     — integer 1..99 (same rule V1 enforced).
 *   4. role      — a postOnly intent whose price would CROSS (taker) is
 *                  refused locally with a truthful message. The venue's
 *                  post-only-cross rejection remains the backstop; role
 *                  `null` (unknown opposing depth) passes — unknown is not
 *                  proof of taker (see limitOrderRole).
 */
import type { VenueBook } from '@/views/ValueDashboard/types';
import { limitOrderRole } from '@/views/ValueDashboard/tradeModalBook';
import {
  classifyLineAvailability,
  LINE_AVAILABILITY_LABEL,
  lineSourceUnconfirmed,
  type LineAvailability,
} from '@/lib/sport/lineAvailability';
import type { Lt2ClockStamp } from './lt2Timing';

/** Names the ACTION that produced the intent, never the key (#725):
 *  hotkey-a/d = passive top/bottom post, hotkey-w/e = attack, hotkey-q/r =
 *  the speed-demon D1 JUMP (re-price the resting order on that slot to the
 *  displayed bid via POST /orders/:id/replace). */
export type Lt2InputMethod =
  | 'mouse'
  | 'hotkey-a'
  | 'hotkey-d'
  | 'hotkey-w'
  | 'hotkey-e'
  | 'hotkey-q'
  | 'hotkey-r';

export type Lt2Surface =
  | 'dash-moneyline'
  | 'dash-multi-outcome'
  | 'dash-spread-total'
  | 'gd-moneyline'
  | 'gd-spread-total'
  | 'gd-game-lines'
  // The single-game live trading view (/live/:gameKey) — ladder/hotkey-native,
  // same capability matrix as the dashboard surfaces (audit keys on
  // marketKind/inputMethod, never on surface).
  | 'live-game';

/**
 * The period an LT2 intent is stamped with.
 *
 * `'F5'` (MLB First 5 innings) exists for ONE surface: the Sniper cockpit's
 * period board constructs its F5 card scope explicitly (Josh, 2026-09-10 —
 * "Tradable: widen to F5"; ProjectBible.StreamRift Log 006). It is deliberately
 * NOT returned by `lt2PeriodFor`, so every surface that infers eligibility from
 * a market's period (GameDetail's Game Lines, the dashboard) keeps the D-020
 * boundary below unchanged. The relay does not gate on period; it records it.
 */
export type Lt2Period = 'full' | '1H' | 'F5';

/** Coverage boundary (release record, D-020): only full-game and 1st-half
 *  derivative markets are one-click eligible. Null = outside the boundary —
 *  callers fall through to the ticket. F5 stays outside HERE on purpose (see
 *  `Lt2Period`); `liveIntent.period.test.ts` pins it. */
export function lt2PeriodFor(period: string): Lt2Period | null {
  return period === 'full' ? 'full' : period === '1H' ? '1H' : null;
}

export interface LiveOrderIntent {
  ticker: string;
  side: 'yes' | 'no';
  priceCents: number;
  postOnly: boolean;
  /** Short human label for the ack line, e.g. "DAL -3.5 NO @ 77¢". */
  label: string;
  inputMethod: Lt2InputMethod;
  surface: Lt2Surface;
  marketKind: 'moneyline' | 'multi-outcome' | 'spread' | 'total';
  period: Lt2Period;
  /** Spread/total line value; null/undefined for moneyline. */
  line?: number | null;
  /** The hotkey focus card that produced this intent, if any. */
  focusedCardId?: string | null;
  /** Date.now() when the displayed price this intent copies was rendered. */
  displayedPriceMs: number;
  /** Date.now() at the DOM input event (keydown/click) that produced this
   *  intent — the anchor for acknowledgement-parity timing. */
  inputAtMs?: number;
  /** Same-page dual-clock stamp for local acceptance telemetry only. The
   *  audit builder and order payload must not copy it. */
  inputAtClock?: Lt2ClockStamp;
  /** Live Trading V3 Attack provenance — present ONLY on hotkey-w/e intents.
   *  Rides into the `__attack` relay marker (gate input) and the audit stamp.
   *  reference = the best same-side displayed bid the target was computed
   *  from; depth = levels above it (fixed at 1 in this release). */
  attack?: {
    depth: number;
    referencePriceCents: number;
  };
  /** #724 — present ONLY on a leg of a two-sided (S) press. `id` is minted
   *  once per press and shared by both legs; `leg` is this leg's index in the
   *  declared visible order (0 = top/left, 1 = bottom/right). Rides into the
   *  `__lt2` audit stamp so the durable record can join the pair back
   *  together; the relay validates the shape and refuses anything else. The
   *  leg's `inputMethod` stays the passive action it IS (hotkey-a / hotkey-d):
   *  the group stamp says the two were fired together, never what they are. */
  group?: Lt2GroupStamp;
  /** #1067 — present ONLY on a leg of a Shift+S press: the whole-number
   *  multiple of the one-click size this leg was sent at. Audit evidence, not
   *  a gate input — the order's own count is what every check enforces. Sits
   *  beside `group`, not inside it, so the relay's strict group parser is
   *  untouched; the receipts read it back from the stored request. */
  sizeMultiplier?: number;
}

/** The durable two-sided group stamp (#724). Mirrored by the relay's
 *  `parseLt2GroupMarker`; change both or neither. */
export interface Lt2GroupStamp {
  id: string;
  mode: 'two-sided';
  leg: number;
  of: number;
}

/**
 * Speed-demon D1 — a JUMP: re-price ONE resting order to the displayed bid in
 * a single relay round trip (`POST /api/kalshi/orders/:id/replace`). Every
 * `LiveOrderIntent` field keeps its meaning (`priceCents` = the NEW price the
 * card displays, `postOnly` = join semantics); the three extras name the
 * order being moved so the store can send the replace body and label the ack
 * "44¢ → 46¢". The size is the resting order's remaining, never the one-click
 * size — a jump is a re-price, not a resize.
 */
export interface LiveJumpIntent extends LiveOrderIntent {
  replaceOrderId: string;
  prevPriceCents: number;
  prevRemaining: number;
}

/** Derivative intents belong to the separately entitled LT2 lane on EVERY
 *  input path. Keep this allowlist explicit so a future intent kind cannot
 *  acquire the LT2 audit/capability contract merely by sharing the guarded
 *  submit path — the exclusion is per input path, not per feature: see
 *  `lt2AuditRequired` for the hotkey rule (#647). */
export const LT2_AUDIT_MARKET_KINDS = new Set<LiveOrderIntent['marketKind']>([
  'spread',
  'total',
]);

/** Whether this intent must carry the `__lt2` audit/capability stamp.
 *
 *  Two ways in, deliberately (#647): every intent of an LT2 market kind, on
 *  any input path — and every HOTKEY intent, on any market kind. A/D orders
 *  are the LT2 hotkey capability itself, so they carry the relay-enforced
 *  entitlement, explicit size ceiling, and receipt input tag even where the
 *  market kind (moneyline) stays outside the kind allowlist. Mouse moneyline
 *  clicks remain unmarked — widening the KIND set would change ordinary click
 *  trading's relay regime, which #647 forbids. */
export function lt2AuditRequired(
  intent: Pick<LiveOrderIntent, 'marketKind' | 'inputMethod'>,
): boolean {
  return LT2_AUDIT_MARKET_KINDS.has(intent.marketKind) || intent.inputMethod !== 'mouse';
}

export type IntentGuardOutcome =
  | { ok: true; role: 'maker' | 'taker' | null; availability: 'active' }
  | {
      ok: false;
      code: Exclude<LineAvailability, 'active'> | 'taker-on-postonly' | 'price-range';
      message: string;
    };

/**
 * Why a one-click or hotkey price cannot be sent, or null when it can: a
 * whole cent from 1 to 99. D20 — a sub-cent price inside that range is a real
 * market price the ticket can send, so it is named and the ticket is the
 * stated way through; anything else is simply out of range.
 */
export function intentPriceRefusal(priceCents: number): string | null {
  if (Number.isInteger(priceCents) && priceCents >= 1 && priceCents <= 99) return null;
  if (Number.isFinite(priceCents) && priceCents > 1 && priceCents < 99) {
    return `Whole-cent prices only for hotkeys/one-click (${priceCents}¢); use the ticket.`;
  }
  return 'Price out of range';
}

export function evaluateIntentGuard(
  intent: LiveOrderIntent,
  book: VenueBook | null | undefined,
): IntentGuardOutcome {
  // Identity first: a book for a different ticker means the click resolved
  // through a mismatched card/row — fail closed as a mapping error.
  if (book != null && book.marketTicker !== intent.ticker) {
    return {
      ok: false,
      code: 'mapping-error',
      message: 'Market identity mismatch — click did not resolve to the displayed market',
    };
  }
  const availability = classifyLineAvailability(book);
  if (availability !== 'active') {
    // Same predicate as the classifier: the governing proof (book when held,
    // row otherwise) is what "source confirmation" refers to.
    const sourceUnconfirmed = lineSourceUnconfirmed(book);
    const reason = sourceUnconfirmed && availability === 'stale'
      ? 'Source confirmation unavailable' : LINE_AVAILABILITY_LABEL[availability];
    return {
      ok: false,
      code: availability,
      message:
        sourceUnconfirmed
          ? `${reason} — order not sent`
        : availability === 'stale'
          ? 'Market is stale — trading is unavailable until the feed resumes'
          : `${reason} — order not sent`,
    };
  }
  const priceRefusal = intentPriceRefusal(intent.priceCents);
  if (priceRefusal != null) {
    return { ok: false, code: 'price-range', message: priceRefusal };
  }
  const role = limitOrderRole(book ?? undefined, intent.side, intent.priceCents);
  if (intent.postOnly && role === 'taker') {
    return {
      ok: false,
      code: 'taker-on-postonly',
      message: 'Price would cross the book — maker order not sent',
    };
  }
  return { ok: true, role, availability: 'active' };
}

/** The `__lt2` audit stamp stored verbatim in trades.request_payload (the
 *  relay's venue-body allowlist keeps it off the wire). Everything the
 *  packet's telemetry section asks of the client side, in one place. */
export function buildLt2AuditMeta(
  intent: LiveOrderIntent,
  guard: Extract<IntentGuardOutcome, { ok: true }>,
  book: VenueBook | null | undefined,
): Record<string, unknown> {
  return {
    v: 1,
    input: intent.inputMethod,
    surface: intent.surface,
    marketKind: intent.marketKind,
    period: intent.period,
    ...(intent.line != null ? { line: intent.line } : {}),
    ...(intent.focusedCardId != null ? { focusedCardId: intent.focusedCardId } : {}),
    displayedPriceMs: intent.displayedPriceMs,
    ...(intent.inputAtMs != null ? { inputAtMs: intent.inputAtMs } : {}),
    clickMs: Date.now(),
    ...(intent.attack != null ? { attack: { ...intent.attack } } : {}),
    ...(intent.group != null ? { group: { ...intent.group } } : {}),
    ...(intent.sizeMultiplier != null ? { sizeMultiplier: intent.sizeMultiplier } : {}),
    guard: {
      availability: guard.availability,
      role: guard.role,
      freshness: book?.freshness ?? null,
    },
  };
}
